import React from "react";
import { Link } from "react-router-dom";

import type { ClientId } from "../domain/types";
import type { ZohoPricingConfigDTO } from "../data/onboardingApi";
import { planById } from "../domain/zohoSizing";
import { CO_BASE } from "../domain/steps";
import { BadgeCheckIcon } from "./icons";

/**
 * Read-only summary of the plan decided at Package sizing, with a link back to
 * change it.
 *
 * Shown on both Zoho account and Add Zournal, from here rather than a copy in
 * each, so the seat arithmetic can't say one thing on the screen where the CA
 * picks a plan and another on the screen where they act on it.
 *
 * The design repo's version carries the seat address inside this card. Ours
 * does not, and must not: the address is a real reserved seat, and Add Zournal
 * holds it back until the client's organisation id is known. A copy of it in a
 * card shown one step earlier would hand it over before that gate exists. The
 * step that has passed the gate renders it itself, as ZohoInviteFields.
 */
export function ChosenPlanSummary({
  clientId,
  config,
  chosenPlan,
  userCount,
}: {
  clientId: ClientId;
  config: ZohoPricingConfigDTO;
  chosenPlan: string;
  userCount: number;
}) {
  const plan = planById(config, chosenPlan);
  const totalSeats = userCount + config.settings.reserved_seats;

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border">
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex size-[38px] shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <BadgeCheckIcon className="size-5 text-primary" />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">Zoho plan, from package sizing</span>
            <h3 className="text-[17px] font-medium">{plan ? plan.label : chosenPlan}</h3>
          </div>
        </div>
        <Link
          to={`${CO_BASE}/${clientId}/package-sizing`}
          className="rounded-md border px-3 py-[5px] text-[13px] font-medium transition-colors hover:bg-muted/40"
        >
          Change
        </Link>
      </div>

      <div className="grid grid-cols-2 items-stretch border-t sm:grid-cols-4">
        <div className="flex flex-col gap-1 px-5 py-[14px]">
          <span className="text-xs text-muted-foreground">This client's users</span>
          <span className="text-[22px] font-medium tabular-nums">{userCount}</span>
        </div>
        <div className="flex flex-col gap-1 border-l px-5 py-[14px]">
          <span className="text-xs text-muted-foreground">Your firm's access</span>
          <span className="text-[22px] font-medium tabular-nums">1</span>
        </div>
        <div className="flex flex-col gap-1 border-l px-5 py-[14px]">
          <span className="text-xs text-muted-foreground">Zournal system user</span>
          <span className="text-[22px] font-medium tabular-nums">1</span>
        </div>
        <div className="flex flex-col gap-1 bg-primary/10 px-5 py-[14px]">
          <span className="text-xs text-primary">Seats needed</span>
          <span className="text-[22px] font-medium tabular-nums text-primary">{totalSeats}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Stand-in for the plan card on a client who hasn't been through Package
 * sizing. A link rather than the question again: sizing is that step's job,
 * and asking here would give us two places that can disagree.
 */
export function SizingPendingCard({ clientId }: { clientId: ClientId }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed p-5">
      <p className="text-sm text-muted-foreground">
        Select the Zoho Books subscription package to see the recommendation.
      </p>
      <Link
        to={`${CO_BASE}/${clientId}/package-sizing`}
        className="w-fit text-sm font-medium underline underline-offset-2"
      >
        Go to Select the Correct Zoho Books Subscription Package
      </Link>
    </div>
  );
}
